export interface SeparationContext { scopeId: string; network: string; role: string; }
export interface TestResult { name: string; status: "passed" | "failed" | "skipped"; detail: string; }
export type Probe = (path: string, method: string, network?: string) => Promise<number>;

export async function runSeparationTests(surface: "admin" | "application", context: SeparationContext,
  probe: Probe, foreignPlanId = ""): Promise<TestResult[]> {
  const results: TestResult[] = [];
  const prefix = `/${surface}/separation-test`;
  const check = async (name: string, path: string, expected: number[], method = "GET", network?: string) => {
    try {
      const status = await probe(path, method, network);
      const passed = expected.includes(status);
      results.push({ name, status: passed ? "passed" : "failed", detail: `Svar ${status}. Forventet: ${expected.join(" eller ")}.` });
      return passed;
    } catch {
      results.push({ name, status: "failed", detail: "Kunne ikke kontakte API'et. Ingen adgangskontrol er bekræftet." });
      return false;
    }
  };
  // An expired token or unavailable module must not make all rejection tests look successful.
  if (!await check("Adgang til eget testmodul", `${prefix}/context`, [200])) return results;
  await check(surface === "admin" ? "Eget netværk er tilgængeligt" : "Egen virksomhed er tilgængelig",
    `${prefix}/scope/${encodeURIComponent(context.scopeId)}`, [200]);
  await check("Et uvedkommende område afvises", `${prefix}/scope/00000000-0000-0000-0000-000000000000`, [403]);
  const other = surface === "admin" ? "application" : "admin";
  await check("Samme login kan ikke bruge den anden testside", `/${other}/separation-test/context`, [403], "GET", context.network);
  await check("Forsøg på at skifte netværk afvises", `${prefix}/context`, [401, 403], "GET", `separation-test-other-${context.network}`);
  await check("Virksomhedsfunktionens normale læseadgang fungerer",
    surface === "admin" ? "/admin/companies?limit=10&page=0" : "/application/business-plans", [200], "GET", context.network);
  if (surface === "application") {
    await check(context.role === "READER" ? "Læser får afvist redigering (uden at gemme)" : "Redigeringsrolle tillades (uden at gemme)",
      `${prefix}/write-check/${encodeURIComponent(context.scopeId)}`, context.role === "READER" ? [403] : [201], "POST", context.network);
    await check("Virksomhedsbruger får afvist adminlisten", "/admin/companies?limit=10&page=0", [403], "GET", context.network);
  }
  if (foreignPlanId.trim()) {
    await check("Den angivne fremmede plan afvises", `/${surface}/business-plans/${encodeURIComponent(foreignPlanId.trim())}`,
      surface === "admin" ? [404] : [403], "GET", context.network);
  } else results.push({ name: "Adgang til en konkret fremmed plan", status: "skipped",
    detail: "Angiv et eksisterende plan-id fra et andet netværk (admin) eller en anden virksomhed (app) for at udføre denne kontrol." });
  return results;
}
